import { format } from 'date-fns';
import { getOnStorage, setOnStorage } from '@/shared';
import { useCallback, useMemo, useState } from 'react';
import { OrderItemsProps } from '../orderStatusPage/useOrderStatusPage';
import { useClient, useFetchAxios, useGlobals, useNetworkStatusContext, useUserContext } from '@/hooks';
import { unregisterServiceWorker } from '@/shared/serviceWorkFunctions';

interface HookProps {
  order?: OrderItemsProps;
  closeModal?: () => void;
  companyData?: {
    clientName: string;
    clientRefNumber: string;
    clientCnpj: string;
    clientUnilever: boolean;
  };
}

function useOrderStatusModal(props = {} as HookProps) {
  const { order, companyData, closeModal } = props;

  const { version } = useGlobals();
  const { axiosCtx } = useFetchAxios();
  const { customer } = useUserContext();
  const { setPositivacao, selectedClient } = useClient();
  const { connectivityStatus } = useNetworkStatusContext();

  const [loading, setLoading] = useState(false);

  const orderPayload = useMemo(() => {
    if (order) {
      const items = order?.actionsPending?.map((item) => ({
        cod_segmento: item?.cod_segmento,
        sku: item?.sku,
        product: item?.product,
        quantity: item?.quantity,
        quantity_multiplo: item?.quantityMultiplo,
        percentual_flex: item?.percentualFlex || '0',
        integration_price: item?.integrationPrice,
        integration_price_flex: item?.integrationPriceFlex || 0,
        price: item?.price,
        unit_price: item?.unitPrice,
        unit_price_per_item: item?.unitPricePerItem,
        st_price: item?.stPrice,
        st_price_packaging: item?.stPricePackaging,
        packaging: item?.packaging,
        reason: '',
        status: '',

        unilever: item?.UNILEVER,
        price_flex: item?.price_flex,
        st_price_flex: item?.st_price_flex,
        unit_price_flex: item?.unit_price_flex,
        unit_price_per_item_flex: item?.unit_price_per_item_flex,
      }));

      return {
        version,
        origin_function: 'reprocess',
        current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        company: {
          Cod_rca: customer?.codPessoa?.toString(),
          cnpj: companyData?.clientCnpj,
          name: companyData?.clientName,
          id: companyData?.clientRefNumber,
          unilever: companyData?.clientUnilever,
        },
        orders: {
          actions_pending: items,
          order_date: order?.orderDate,
          order_number: order?.orderNumber,
          order_number_magento: order?.orderNumberMagento,
          erp_order_number: order?.erpNumber,
          origin: 'BateForte_web_mobile_rca',
          items: items?.length,
          total: order?.total,
          payment_terms: order?.paymentTerms,
          status: '',
          reason: '',
        },
      };
    }
  }, [order, customer?.codPessoa, companyData?.clientCnpj, companyData?.clientName, companyData?.clientRefNumber]);

  const handleReprocessOrder = useCallback(async () => {
    if (!orderPayload) {
      console.log('Order payload is not ready');
      return;
    }

    if (connectivityStatus === 'online') {
      try {
        setLoading(true);
        const orderFromStorage = await getOnStorage('offline_orders');
        const synched = await getOnStorage('synched_orders');
        let synchedOrders = [];

        if (synched && synched !== undefined && synched !== 'undefined' && synched?.length) {
          synchedOrders = synched;
        }

        const orderIndex = orderFromStorage?.findIndex(
          (i: any) =>
            i?.orders?.order_number === orderPayload?.orders?.order_number && i?.company?.id === orderPayload?.company?.id
        );

        if (orderIndex > -1) {
          orderFromStorage.splice(orderIndex, 1);
          synchedOrders?.push({
            ...orderPayload,
            orders: {
              ...orderPayload?.orders,
              status: 'processando',
            },
          });
          setOnStorage('offline_orders', orderFromStorage);
          setOnStorage('synched_orders', synchedOrders);
        }

        const ok = await axiosCtx
          .post('/integra-pedido', orderPayload)
          .then((res) => Boolean(res?.data?.message))
          .catch((error) => {
            console.error('Falha ao reenviar pedido:', error);
            return false;
          });

        if (ok) {
          const skus = orderPayload?.orders?.actions_pending?.map((i) => Number(i.sku)) || [];
          setPositivacao('produto', skus);
          setPositivacao('cliente', [Number(selectedClient?.COD_CLIENTE)]);
          await new Promise((resolve) => setTimeout(() => resolve(true), 1000));
          closeModal && closeModal();
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      } finally {
        unregisterServiceWorker({ preserveLocalStorage: true });
      }
    }
  }, [orderPayload, connectivityStatus, axiosCtx, selectedClient, closeModal, setPositivacao]);

  return {
    loading,
    handleReprocessOrder,
  };
}

export default useOrderStatusModal;
